// src/pages/ProductTypeForm.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ProductTypeService, ProductTypeFormData } from "../services/productTypeService";

interface ProductTypeFormState extends ProductTypeFormData {}

const ProductTypeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const editMode = !!id; // Edit mode if there is an id param
  const navigate = useNavigate();

  const [form, setForm] = useState<ProductTypeFormState>({
    name: "",
    description: "",
  });
  const [loading, setLoading] = useState(editMode); // only load if edit mode
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  // Load existing product type if edit mode
  useEffect(() => {
    if (!editMode) return;

    const fetchProductType = async () => {
      try {
        const data = await ProductTypeService.getById(Number(id));
        setForm({ name: data.name, description: data.description || "" });
      } catch (err) {
        console.error(err);
        alert("Failed to load product type");
        navigate("/product-types");
      } finally {
        setLoading(false);
      }
    };

    fetchProductType();
  }, [editMode, id, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");

    try {
      if (editMode) {
        await ProductTypeService.update(Number(id), form);
        setMessage("✅ Product type updated successfully");
      } else {
        await ProductTypeService.create(form);
        setMessage("✅ Product type created successfully");
        setForm({ name: "", description: "" });
      }
    } catch (err) {
      console.error(err);
      setMessage("❌ Failed to save product type");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this product type?")) return;

    try {
      await ProductTypeService.delete(Number(id));
      alert("✅ Product type deleted successfully");
      navigate("/product-types");
    } catch (err) {
      console.error(err);
      alert("❌ Failed to delete product type");
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-lg bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          {editMode ? "Edit Product Type" : "Create Product Type"}
        </h1>
        <p className="text-sm text-gray-500 mb-4">
          {editMode
            ? "Update the details of this product type"
            : "Add a new product type to your catalog"}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring focus:ring-indigo-200"
            />
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring focus:ring-indigo-200"
            />
          </div>

          {/* Message */}
          {message && (
            <p className="text-sm text-gray-700">{message}</p>
          )}

          {/* Actions */}
          <div className="flex justify-between gap-2">
            <button
              type="button"
              onClick={() => navigate("/product-types")}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>

            <div className="flex gap-2">
              {editMode && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </button>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting
                  ? editMode
                    ? "Updating..."
                    : "Creating..."
                  : editMode
                  ? "Update"
                  : "Create"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductTypeForm;
